import { z } from 'zod';
import { FIELD_TYPE } from './types';
import type { FieldType } from './types';

export const fieldOptionSchema = z.object({
  /** Present only for an option that already exists on the backend — absent for a new, unsaved one. */
  id: z.string().optional(),
  name: z
    .string()
    .min(1, { error: 'customFields.form.validation.optionNameRequired' })
    .max(100, { error: 'customFields.form.validation.optionNameTooLong' }),
  value: z
    .string()
    .min(1, { error: 'customFields.form.validation.optionValueRequired' })
    .max(100, { error: 'customFields.form.validation.optionValueTooLong' }),
  color: z.string().optional(),
  orderIndex: z.number(),
});

export type FieldOptionInput = z.infer<typeof fieldOptionSchema>;

const validFieldTypes = Object.values(FIELD_TYPE) as number[];

/**
 * Validation schema for custom field creation.
 *
 * @remarks
 * - `type` is validated against `FIELD_TYPE` numeric values — arbitrary numbers are rejected.
 * - `defaultValue` is always a string: checkbox stores `"true"` / `"false"`, number/date store their string representations.
 * - `typeConfig` must be a JSON string if provided — validated at the service boundary, not here.
 * - `options` is only meaningful for Select and MultiSelect types; ignored by the backend for other types.
 */
export const createFieldSchema = z.object({
  name: z
    .string()
    .min(1, { error: 'customFields.form.validation.nameRequired' })
    .max(200, { error: 'customFields.form.validation.nameTooLong' }),
  type: z.number().refine((v) => validFieldTypes.includes(v), {
    error: 'customFields.form.validation.invalidType',
  }),
  description: z
    .string()
    .max(500, { error: 'customFields.form.validation.descriptionTooLong' })
    .optional(),
  defaultValue: z.string().optional(),
  required: z.boolean().default(false),
  pinned: z.boolean().default(false),
  visibleToGuests: z.boolean().default(false),
  order: z.number().default(0),
  typeConfig: z.string().optional(),
  options: z.array(fieldOptionSchema).optional(),
  /** Form-only — merged into `typeConfig` on submit, only meaningful for `Currency`. */
  currencyCode: z.string().optional(),
});

export type CreateFieldInput = z.input<typeof createFieldSchema>;

export const updateFieldSchema = z.object({
  name: z
    .string()
    .min(1, { error: 'customFields.form.validation.nameRequired' })
    .max(200, { error: 'customFields.form.validation.nameTooLong' })
    .optional(),
  description: z
    .string()
    .max(500, { error: 'customFields.form.validation.descriptionTooLong' })
    .optional(),
  defaultValue: z.string().optional(),
  required: z.boolean().optional(),
  pinned: z.boolean().optional(),
  visibleToGuests: z.boolean().optional(),
  order: z.number().optional(),
  typeConfig: z.string().optional(),
  options: z.array(fieldOptionSchema).optional(),
});

export type UpdateFieldInput = z.infer<typeof updateFieldSchema>;

export const upsertFieldValueSchema = z.object({
  value: z.string(),
});

export type UpsertFieldValueInput = z.infer<typeof upsertFieldValueSchema>;

/**
 * Partial update for a single `Select`/`MultiSelect` option via
 * `PUT /projects/{projectId}/fields/{fieldId}/options/{optionId}`.
 *
 * @remarks
 * `color` is tri-state on the wire: omit the key to leave it unchanged, send `null` to
 * clear it. `z.string().nullable().optional()` preserves that distinction — `undefined`
 * (key omitted) and `null` (explicit clear) serialize differently via `JSON.stringify`.
 */
export const editFieldOptionSchema = z.object({
  name: z
    .string()
    .min(1, { error: 'customFields.form.validation.optionNameRequired' })
    .max(100, { error: 'customFields.form.validation.optionNameTooLong' })
    .optional(),
  value: z
    .string()
    .min(1, { error: 'customFields.form.validation.optionValueRequired' })
    .max(255, { error: 'customFields.form.validation.optionValueTooLong' })
    .optional(),
  color: z.string().nullable().optional(),
  orderIndex: z.number().optional(),
});

export type EditFieldOptionInput = z.infer<typeof editFieldOptionSchema>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^\+[1-9]\d{7,14}$/;

function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isBlankValue(fieldType: FieldType, value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return true;
  if ((fieldType === FIELD_TYPE.MultiSelect || fieldType === FIELD_TYPE.Users) && trimmed === '[]')
    return true;
  return false;
}

/**
 * Client-side mirror of the backend's per-type value validation (see
 * `frontend-spec-custom-project-fields.md` §2). Runs before `upsertTaskFieldValue` so
 * the user gets instant feedback instead of a round-trip 400.
 *
 * @remarks
 * - Returns an i18n key on failure, `null` when the value is valid.
 * - `Files`/`Signature` are never validated here — their value is backend-constructed
 *   and can only be set through the file-upload endpoint.
 * - `Users` stays read-only until the member picker is implemented, so it is not validated.
 */
export function validateTaskFieldValue(
  fieldType: FieldType,
  value: string,
  required: boolean,
): string | null {
  if (required && isBlankValue(fieldType, value)) {
    return 'customFields.fieldValue.validation.required';
  }

  if (isBlankValue(fieldType, value)) return null;

  switch (fieldType) {
    case FIELD_TYPE.Number:
      return Number.isNaN(Number(value))
        ? 'customFields.fieldValue.validation.invalidNumber'
        : null;

    case FIELD_TYPE.Currency: {
      const parsed = Number(value);
      if (Number.isNaN(parsed)) return 'customFields.fieldValue.validation.invalidNumber';
      return parsed < 0 ? 'customFields.fieldValue.validation.invalidNegative' : null;
    }

    case FIELD_TYPE.Checkbox:
      return value === 'true' || value === 'false'
        ? null
        : 'customFields.fieldValue.validation.invalidCheckbox';

    case FIELD_TYPE.Email:
      return EMAIL_PATTERN.test(value) ? null : 'customFields.fieldValue.validation.invalidEmail';

    case FIELD_TYPE.Url:
      return isValidUrl(value) ? null : 'customFields.fieldValue.validation.invalidUrl';

    case FIELD_TYPE.Phone:
      return PHONE_PATTERN.test(value) ? null : 'customFields.fieldValue.validation.invalidPhone';

    default:
      return null;
  }
}
